import { defineStore } from 'pinia'
import { useNetworkStore } from '@/stores/network/store'
import { useExtensionStore, type Group } from '@/stores/extension/store'
import {
  reduceIncomingExtension,
  transformIncomingExtension,
} from '@/utils/dataTransformers'
import * as Api from './api'
import {
  callWithMeta,
  createMeta,
  getMetaInitial,
  waitFor,
  type Meta,
} from '@/utils/store'
import { useDevicesStore } from '@/stores/devices/store'
import { useDeviceStore } from '@/stores/device/store'
import * as ApiExtension from '@/stores/extension/api'
import { useSoftkeysStore } from '@/stores/softkeys/store'
import { usePromptsStore } from '@/stores/prompts/store'
import { useAudioFileStore } from '@/stores/audioFile/store'
import type { ColorName } from '@/utils/colors'

export type Extension = {
  id: number
  name: string
  firstname?: string | null
  lastname?: string | null
  trunkId: number
  type: ApiExtension.Extension['type']
  quickDial: string
  directDial: string
  isMsn: boolean
  color?: ColorName | null
  privacy: boolean
  dnd: boolean
  email?: string | null
  smsNumber?: string | null
  forwardingType: string
  showMissedCalls: boolean
  isClosingActive: boolean
  calleridForwardType: string
  conference?: {
    id: number
    config: string
  } | null
  e164: string
  setNameAsPrefix: boolean
  directDialing: string
  isBusyOnBusy: boolean
  callflowId: number | null
  devices: number[]
  promptForwardingId?: number
  editable: boolean
  isTrunkExisting?: boolean
  isTrunkInPbxMode?: boolean
  onlyForwardExternal: boolean
  blockIncomingExternal: boolean
  ignoreNightswitchInternal: boolean
  externalReference: string | null
  fqdn?: string
  assigned: boolean
  extendedForwardingProfiles: any[]
  isSingleNumber?: boolean
  conferencePin: string
  groups: Group[]
  userExtensions: number[]

  // Unset values
  trunk?: {
    id: number
    username: string
    label: string
    isBlockNumber: boolean
    isPartialBlock: boolean
    blockRangeStart: number
    blockRangeEnd: number
    blockSize: number
    customer: number
  }
  permissionsToEdit?: []
}

type State = {
  extensions: Extension[]
  meta: Meta<{
    extensions: undefined
    load: undefined
    changeColor: undefined
    update: undefined
    delete: undefined
  }>
}

const getDefaultState = (): State => ({
  extensions: [],
  meta: createMeta(['extensions', 'load', 'changeColor', 'update', 'delete']),
})

export const useExtensionsStore = defineStore('extensions', {
  state: getDefaultState,

  getters: {
    find: (state) => (id: number | string) =>
      state.extensions.find((extension) => extension.id === id),
    list: (state) => state.extensions,
  },

  actions: {
    RESET() {
      this.extensions = []
      this.meta.extensions = getMetaInitial()
    },

    SET_GROUP_TAGS(payload: any) {
      this.extensions.forEach((extension) => {
        const sameGroups: any[] = []
        let deleteGroup = false

        extension.groups?.forEach((group) => {
          if (group.label === payload.label && group.color === payload.color) {
            sameGroups.push(group.id)
          }
          if (
            group.group_tag_id ===
            (payload.old_id ? payload.old_id : payload.id)
          ) {
            Object.assign(group, {
              label: payload.label,
              color: payload.color,
              group_tag_id: payload.id,
            })
            deleteGroup = true
          }
        })
        if (deleteGroup) {
          extension.groups = extension.groups?.filter(
            (group) => !sameGroups.includes(group.id)
          )
        }
      })
    },

    DELETE_GROUP_TAGS(payload: any) {
      this.extensions.map((extension) => {
        extension.groups = extension.groups?.filter(
          (group) => group.group_tag_id != payload
        )
      })
    },

    DELETE_GROUP_TAG(payload: any) {
      this.extensions.map((extension) => {
        extension.groups = extension.groups?.filter(
          (group) => group.id != payload.id
        )
      })
    },

    ADD_GROUP_TAG(payload: any) {
      this.extensions.map((extension) => {
        if (extension.id === payload.elementId) {
          delete payload.elementId

          const exists = extension.groups.filter(
            (group) => group.group_tag_id === payload.group_tag_id
          )

          if (!exists.length) {
            extension.groups.push(payload)
          }
        }
      })
    },

    async load({ useCache = true } = {}) {
      const hasCached = ['pending', 'success'].includes(
        this.meta.extensions.status
      )

      if (useCache && hasCached) {
        await waitFor(() => this.meta.extensions.status !== 'pending')

        return
      }

      await callWithMeta({
        store: this,
        metaFields: { extensions: undefined, load: undefined },
        apiCall: async () => Api.loadExtensions(),
        onSuccess: ({ result, isLatest }) => {
          if (isLatest('extensions')) {
            this.extensions = result.payload.map((extension) =>
              reduceIncomingExtension(transformIncomingExtension(extension))
            )
          }
        },
        onError: ({ result, isLatest }) => {
          if (isLatest('load')) {
            useNetworkStore().HANDLE_LOADING_ERROR(result.payload)
          }
        },
      })
    },

    async changeColor({
      color,
      itemId,
    }: {
      color: ColorName | null
      itemId: number
    }) {
      // Show color change before waiting for API call

      const extensionIndex = this.extensions.findIndex(
        (extension) => extension.id === itemId
      )

      if (extensionIndex >= 0) {
        this.extensions[extensionIndex].color = color
      }

      // Send color change to the BE

      await callWithMeta({
        store: this,
        metaFields: { changeColor: undefined },
        apiCall: async () =>
          ApiExtension.updateExtension(itemId, {
            color,
          }),
        onSuccess: ({ result, isLatest }) => {
          if (isLatest('changeColor')) {
            const updatedExtension = reduceIncomingExtension(
              transformIncomingExtension(result.payload)
            )

            const extensionIndex = this.extensions.findIndex(
              (extension) => extension.id === updatedExtension.id
            )

            this.extensions[extensionIndex] = updatedExtension
          }
        },
      })
    },

    async update(data: {
      extensionType: string
      extensionIds: number[]
      preferences: {
        name: string
        value: any
      }[]
    }) {
      const { result } = await callWithMeta({
        store: this,
        metaFields: { extensions: undefined, update: undefined },
        apiCall: async () => Api.updateExtensions(data),
        onSuccess: ({ isLatest }) => {
          if (isLatest('update')) {
            this.load({ useCache: false })

            useExtensionStore().reload()

            useDevicesStore().load({ useCache: false })

            useDeviceStore().reload()

            usePromptsStore().load({ useCache: false })

            useAudioFileStore().reload()
          }
        },
      })

      if (result.status === 'success') {
        return result.payload
      }

      throw result.payload
    },

    async delete(extensionIds: number[]) {
      const { result } = await callWithMeta({
        store: this,
        metaFields: { extensions: undefined, delete: undefined },
        apiCall: async () => Api.deleteExtensions(extensionIds),
        onSuccess: ({ isLatest }) => {
          if (isLatest('extensions')) {
            this.extensions = this.extensions.filter(
              (extension) => !extensionIds.includes(extension.id)
            )
          }

          if (isLatest('delete')) {
            if (
              extensionIds.includes(
                Number(useExtensionStore().meta.extensionData.params?.id)
              )
            ) {
              useExtensionStore().RESET()
            }

            useDevicesStore().load({ useCache: false })

            useDeviceStore().reload()

            usePromptsStore().load({ useCache: false })

            useAudioFileStore().reload()

            useSoftkeysStore().RESET()
          }
        },
      })

      if (result.status === 'success') {
        return result.payload
      }

      throw result.payload
    },
  },
})
